import React, { useCallback, useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { useIsReducedMotion } from '@atlaskit/motion';
import {
	Spotlight,
	SpotlightManager,
	SpotlightPulse,
	SpotlightTransition,
} from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import spotlightImage from './assets/pageHeaderSpotlightImage.png';

const i18n = defineMessages({
	body: {
		id: 'loom-onboarding.inline-comment-button-spotlight.content',
		defaultMessage: 'Walk through your questions or comments with async video.',
		description: 'Text for the inline comment Loom record button spotlight',
	},
	dismissButton: {
		id: 'loom-onboarding.inline-comment-button-spotlight.dismissButton',
		defaultMessage: 'Dismiss',
		description: 'Text on the button to click to indicate you are done viewing the spotlight',
	},
	tryItOutButton: {
		id: 'loom-onboarding.inline-comment-button-spotlight.tryItOutButton',
		defaultMessage: 'Try it out',
		description: 'Text on the button to click to try recording a Loom',
	},
});

const SCREEN_NAME = 'inlineCommentLoomButtonOnboardingSpotlight';

export type InlineCommentLoomButtonSpotlightProps = {
	/**
	 * spotlight trigger component.
	 */
	children?: React.ReactNode;
	/**
	 * spotlight dismiss button callback.
	 */
	onClose: () => void;
	/**
	 * spotlight "Try it out" button callback.
	 */
	onTryClick: () => void;
	/**
	 * The element that we should attach our spotlight to. Is one of the many inline comment containers.
	 */
	commentContainerElement: HTMLElement;
};

export const InlineCommentLoomButtonSpotlight = ({
	children,
	onClose,
	onTryClick,
	commentContainerElement,
}: InlineCommentLoomButtonSpotlightProps) => {
	const isReducedMotion = useIsReducedMotion();
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isClosed = useRef<boolean>(false);

	const close = useCallback(() => {
		if (!isClosed.current) {
			isClosed.current = true;
			onClose?.();
		}
	}, [onClose]);

	// Call `onClose()` when this component unmounts if it wasn't already closed in order to catch when the spotlight
	// disappears if users click outside of it. This is important because we need the parent to know when to call `stop()`
	// for the EP message so we don't block other onboarding from appearing for 5 min (the default expiration time)
	useEffect(() => {
		return () => {
			close();
		};
	}, [close]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: SCREEN_NAME,
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleDismiss = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismiss',
				source: SCREEN_NAME,
			},
		}).fire();
		close();
	}, [close, createAnalyticsEvent]);

	const handleTryItOut = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'tryItOut',
				source: SCREEN_NAME,
			},
		}).fire();
		onTryClick();
		close();
	}, [close, onTryClick, createAnalyticsEvent]);

	return (
		<SpotlightManager onBlanketClicked={handleDismiss}>
			<SpotlightPulse radius={6} pulse={!isReducedMotion}>
				{children}
			</SpotlightPulse>
			<SpotlightTransition>
				<Spotlight
					targetBgColor={token('color.text.inverse')}
					targetNode={commentContainerElement}
					pulse={false}
					targetRadius={6}
					dialogWidth={275}
					image={spotlightImage}
					dialogPlacement="left bottom"
					actions={[
						{
							text: intl.formatMessage(i18n.tryItOutButton),
							onClick: handleTryItOut,
						},
						{
							text: intl.formatMessage(i18n.dismissButton),
							onClick: handleDismiss,
							appearance: 'subtle',
						},
					]}
				>
					<FormattedMessage {...i18n.body} />
				</Spotlight>
			</SpotlightTransition>
		</SpotlightManager>
	);
};
